import AppSignal, { type Span } from '@appsignal/javascript';
import React from 'react';

type Props = {
  instance: AppSignal;
  children: React.ReactNode;
  fallback?: (error?: Error) => React.ReactNode;
  override?: (span: Span, error?: Error) => Span;
  tags?: Record<string, string>;
};

type State = {
  error: Error | undefined;
};

/**
 * Copied from https://github.com/appsignal/appsignal-javascript/blob/main/packages/react/src/ErrorBoundary.tsx
 * Note that error boundaries have to be class components.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state = { error: undefined };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public componentDidCatch(error: Error): void {
    const { instance: appsignal, tags = {} } = this.props;

    const span = appsignal.createSpan();
    span.setError(error).setTags(tags);

    span.setAction(
      isDynamicLoadError(error) ? 'DynamicLoadError' : 'ErrorBoundary'
    );

    appsignal.send(span);
  }

  public render(): React.ReactNode {
    if (this.state.error) {
      return this.props.fallback ? this.props.fallback(this.state.error) : null;
    }

    return this.props.children;
  }
}

export function isDynamicLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const msg = error.message;

  return (
    // Safari
    (msg.includes('Unable to preload CSS for') ||
    msg.includes('Failed to fetch') ||
    msg.includes('Loading chunk') || msg.includes('Importing a module script failed'))
  );
}
